/**
 * Trader Results (#383) frontend model: the route's response contract, a local
 * pure sorter, and the browser-built CSV of the visible table.
 *
 * The row types mirror relay/src/services/traderResults.ts. The two datasets
 * are alternative accounting grains — fill activity and settled-position
 * results are never additive (docs/trader-results-contract.md).
 */
import { buildCsv, csvCell, csvTextCell } from '../../lib/infra/csvTransport';

export type TraderResultsDataset = 'fills' | 'settlements';
export type TraderResultsVenue = 'all' | 'kalshi' | 'polymarket' | 'polymarket_us';
/** Settled results carry no Kalshi rows; `all` is international Polymarket plus Polymarket US (#1031). */
export type TraderResultsSettlementVenue = 'all' | 'polymarket' | 'polymarket_us';
export type TraderAttribution = 'current-trader' | 'historical-unassigned';

export interface TraderIdentity {
  traderId: string | null;
  trader: string;
  attribution: TraderAttribution;
}

export interface AvailableTrader {
  id: string;
  username: string;
}

export interface FillActivityRow extends TraderIdentity {
  contracts: string;
  executedVolumeUsd: string;
  fills: number;
  filledOrders: number;
  averageExecutionPriceUsd: string;
  knownActualFeesUsd: string;
  unknownFeeFills: number;
  estimatedRebatesUsd: string;
  unknownRebateFills: number;
}

export interface SettlementResultsRow extends TraderIdentity {
  settledBuckets: number;
  /** '' = no known value: settled buckets exist but none carries one (#1031, Polymarket US). */
  tradingRealizedGrossUsd: string;
  residualSettlementPnlGrossUsd: string;
  knownRealizedFeesUsd: string;
  knownTotalRealizedNetUsd: string;
  incompleteEvidenceRows: number;
  wins: number;
  losses: number;
  ties: number;
}

export type FillActivityTotals = Omit<FillActivityRow, keyof TraderIdentity>;
export type SettlementResultsTotals = Omit<SettlementResultsRow, keyof TraderIdentity>;

export type TraderResultsResponse =
  | {
      dataset: 'fills';
      from: string;
      to: string;
      venue: TraderResultsVenue;
      availableTraders: AvailableTrader[];
      rows: FillActivityRow[];
      totals: FillActivityTotals;
      /** Present when Polymarket US is in scope (#1031): US executions left out of these totals for
       *  conflicting, unreadable or unpriced evidence. */
      usWithheldExecutions?: number;
    }
  | {
      dataset: 'settlements';
      from: string;
      to: string;
      venue: TraderResultsSettlementVenue;
      coverage: 'current-positive-residual-snapshot-filtered-by-settled-at';
      availableTraders: AvailableTrader[];
      rows: SettlementResultsRow[];
      totals: SettlementResultsTotals;
    };

export type TraderResultsSortKey =
  | 'trader'
  | keyof FillActivityTotals
  | keyof SettlementResultsTotals;

export type SortDirection = 'asc' | 'desc';

/**
 * Pure local sorter for the visible table. Blank cells (e.g. the average
 * execution price of a zero row) sort to the bottom in BOTH directions; ties
 * break on trader name then trader id so the order is always deterministic.
 */
export function sortTraderResultsRows<T extends FillActivityRow | SettlementResultsRow>(
  rows: readonly T[],
  key: TraderResultsSortKey | null,
  direction: SortDirection,
): T[] {
  const copy = [...rows];
  if (key == null) return copy;
  const multiplier = direction === 'asc' ? 1 : -1;
  return copy.sort((a, b) => {
    let result = 0;
    if (key === 'trader') {
      result = multiplier * a.trader.localeCompare(b.trader);
    } else {
      const left = numericCell(a, key);
      const right = numericCell(b, key);
      if (left == null && right == null) result = 0;
      else if (left == null) result = 1; // blanks last regardless of direction
      else if (right == null) result = -1;
      else result = multiplier * (left - right);
    }
    return result
      || a.trader.localeCompare(b.trader)
      || (a.traderId ?? '').localeCompare(b.traderId ?? '');
  });
}

function numericCell(
  row: FillActivityRow | SettlementResultsRow,
  key: Exclude<TraderResultsSortKey, 'trader'>,
): number | null {
  const value = (row as unknown as Record<string, unknown>)[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

const FILL_CSV_HEADERS = [
  'Trader',
  'Attribution',
  'Executed volume USD',
  'Contracts',
  'Fills',
  'Filled orders',
  'Average execution price USD',
  'Known actual fees USD',
  'Unknown fee fills',
  'Estimated rebates USD',
  'Unknown rebate fills',
] as const;

const SETTLEMENT_CSV_HEADERS = [
  'Trader',
  'Attribution',
  'Settled position buckets',
  'Trading realized gross USD',
  'Residual settlement P&L gross USD',
  'Known realized fees USD',
  'Known total realized net USD',
  'Evidence-gap rows',
  'Wins',
  'Losses',
  'Ties',
] as const;

/**
 * Build the CSV of the CURRENTLY VISIBLE table: callers pass rows in the active
 * sort/filter order and the builder preserves it. Only Trader and Attribution
 * are untrusted human text (csvTextCell); every numeric cell stays raw so
 * negative money is never corrupted with a leading apostrophe.
 */
export function buildTraderResultsCsv(
  dataset: TraderResultsDataset,
  rowsInVisibleOrder: readonly (FillActivityRow | SettlementResultsRow)[],
): string {
  if (dataset === 'fills') {
    const rows = rowsInVisibleOrder as readonly FillActivityRow[];
    return buildCsv(FILL_CSV_HEADERS, rows.map((row) => [
      csvTextCell(row.trader),
      csvTextCell(row.attribution),
      csvCell(row.executedVolumeUsd),
      csvCell(row.contracts),
      csvCell(row.fills),
      csvCell(row.filledOrders),
      csvCell(row.averageExecutionPriceUsd),
      csvCell(row.knownActualFeesUsd),
      csvCell(row.unknownFeeFills),
      csvCell(row.estimatedRebatesUsd),
      csvCell(row.unknownRebateFills),
    ]));
  }
  const rows = rowsInVisibleOrder as readonly SettlementResultsRow[];
  return buildCsv(SETTLEMENT_CSV_HEADERS, rows.map((row) => [
    csvTextCell(row.trader),
    csvTextCell(row.attribution),
    csvCell(row.settledBuckets),
    csvCell(row.tradingRealizedGrossUsd),
    csvCell(row.residualSettlementPnlGrossUsd),
    csvCell(row.knownRealizedFeesUsd),
    csvCell(row.knownTotalRealizedNetUsd),
    csvCell(row.incompleteEvidenceRows),
    csvCell(row.wins),
    csvCell(row.losses),
    csvCell(row.ties),
  ]));
}

/**
 * The Overall settlement export is a filter over the CURRENT materialized
 * snapshot — not a historical reconstruction — so its filename says
 * "current-snapshot-all-settled-dates", never "all-time".
 */
export function traderResultsFilename(
  dataset: TraderResultsDataset,
  fromIso: string,
  toIso: string,
  overall: boolean,
): string {
  if (dataset === 'settlements' && overall) {
    return 'trader-results-settlements-current-snapshot-all-settled-dates.csv';
  }
  return `trader-results-${dataset}-${dateStamp(fromIso)}-to-${dateStamp(toIso)}.csv`;
}

function dateStamp(iso: string): string {
  return iso.slice(0, 10).replace(/-/g, '');
}
